import {Fragment, useState} from 'react'
import {Button, Card, Flex, Skeleton, Table, Text} from '@mantine/core'
import {ConnectionTypeBadge} from '../calendar/components/badges/ConnectionTypeBadge.tsx'
import {NoProjectWarning} from './components/NoProjectWarning.tsx'
import {ProjectBadge} from '../calendar/components/badges/ProjectBadge.tsx'
import {LastRefresh} from './components/lastRefresh/LastRefresh.tsx'
import {RepositoryRows} from './RepositoryRows.tsx'
import {range} from 'lodash'
import {IconPlus} from '@tabler/icons-react'
import {AddConnectionModal} from './modals/AddConnectionModal.tsx'
import {EditConnectionModal} from './modals/EditConnectionModal.tsx'
import {EditRepositoryModal} from './modals/EditRepositoryModal.tsx'
import type {Connection, GitHubRepository} from '@package/shared'
import {useConnections} from '../../providers/connections/useConnections.ts'

type Props = {disabled: boolean}

export const ConnectionsList = ({disabled: containerDisabled}: Props) => {
  const {connections, loading: loadingConnections, saving, refreshingRecommendations} = useConnections()
  const [showAddForm, setShowAddForm] = useState<boolean>(false)
  const [connectionToEdit, setConnectionToEdit] = useState<Connection>()
  const [repositoryToEdit, setRepositoryToEdit] = useState<GitHubRepository>()

  const disabled = loadingConnections || saving || refreshingRecommendations || containerDisabled

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <Card p={0}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Project</Table.Th>
              <Table.Th>
                <Flex justify={'end'}>Last refresh</Flex>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {connections.map((connection) => {
              return (
                <Fragment key={connection.id}>
                  <Table.Tr
                    onClick={(e) => {
                      if (e.isDefaultPrevented()) return
                      setConnectionToEdit(connection)
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    <Table.Td width={25}>
                      <ConnectionTypeBadge type={connection.type} />
                    </Table.Td>

                    <Table.Td width={0}>
                      <Text>{connection.name}</Text>
                    </Table.Td>

                    <Table.Td>
                      <Flex gap={5} wrap={'wrap'}>
                        {'projectId' in connection && !connection.projectId && <NoProjectWarning variant={'tooltip'} />}
                        {'projectId' in connection && connection.projectId && (
                          <ProjectBadge projectId={connection.projectId} />
                        )}
                      </Flex>
                    </Table.Td>

                    <Table.Td>
                      <Flex justify={'end'}>
                        <LastRefresh connection={connection} disabled={disabled} />
                      </Flex>
                    </Table.Td>
                  </Table.Tr>

                  {connection.type === 'github-api' && (
                    <RepositoryRows connection={connection} onClick={setRepositoryToEdit} />
                  )}
                </Fragment>
              )
            })}

            {loadingConnections &&
              range(0, 3).map((i) => (
                <Table.Tr key={i}>
                  <Table.Td width={25}>
                    <Skeleton h={25} w={25} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} maw={300} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} maw={150} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} maw={150} />
                  </Table.Td>
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Button
        variant={'subtle'}
        onMouseDown={() => setShowAddForm(true)}
        disabled={disabled}
        leftSection={<IconPlus stroke={1} />}
      >
        Add connection
      </Button>

      <AddConnectionModal opened={showAddForm} onClose={() => setShowAddForm(false)} />
      <EditConnectionModal
        connection={connectionToEdit}
        opened={!!connectionToEdit}
        onClose={() => setConnectionToEdit(undefined)}
      />
      <EditRepositoryModal
        repository={repositoryToEdit}
        opened={!!repositoryToEdit}
        onClose={() => setRepositoryToEdit(undefined)}
      />
    </div>
  )
}
